/* Dispatch 3.5 -- force-change-password gate.
 *
 * Route: /force-change-password  (auth required, gated in App.js).
 * Purpose: An admin flagged this user to rotate their password on next
 *          login (via reset-password-temp, invite-accept, or manual
 *          toggle). This page is the ONLY route accessible until the
 *          user complies. Router guard in App.js short-circuits every
 *          other navigation to this URL.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Eye, EyeOff, ShieldAlert, Lock, LogOut } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/store/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

export default function ForceChangePassword() {
  const nav = useNavigate();
  const { user, hydrate, logout } = useAuth();
  const [currentPw, setCurrentPw] = useState("");
  const [pw, setPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const canSubmit = pw.length >= 4 && pw === confirmPw && !submitting;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    setErr(null);
    try {
      await api.post("/auth/force-change-password", {
        new_password: pw,
        confirm_password: confirmPw,
        current_password: currentPw || undefined,
      });
      toast.success("Password updated. Welcome in.");
      await hydrate();
      nav("/", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Update failed";
      setErr(detail);
    } finally { setSubmitting(false); }
  };

  const signOut = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#040D1C] via-[#031028] to-[#003771] text-white py-10 px-4"
      data-testid="force-change-page">
      <div className="max-w-md mx-auto flex items-center gap-3 mb-8">
        <Logo size={32} />
        <div>
          <div className="font-heading text-lg uppercase tracking-[0.14em] text-white">Sharda Energy OS</div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/50">One quick step</div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="max-w-md mx-auto glass-elevated p-8 space-y-6">
        <div className="text-center space-y-1">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-primary flex items-center justify-center">
            <ShieldAlert className="h-7 w-7 text-white" />
          </div>
          <div className="font-heading text-xl md:text-2xl uppercase tracking-[0.08em]">Set a new password</div>
          <p className="text-sm text-white/60">
            Your administrator asked you to rotate your password before continuing.
            {user?.email && <> Signed in as <span className="text-white/80">{user.email}</span>.</>}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="force-change-form">
          <div>
            <Label className="text-xs uppercase tracking-widest">Current password (optional)</Label>
            <div className="relative mt-1">
              <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input type={showPw ? "text" : "password"} value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                className="pl-9 bg-white/5"
                placeholder="If you know it, enter it here"
                data-testid="force-change-current-password"
                autoComplete="current-password" />
            </div>
            <div className="text-[11px] text-white/45 mt-1">Not required -- your admin already authorised the reset.</div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest">New password</Label>
            <div className="relative mt-1">
              <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input type={showPw ? "text" : "password"} value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="pl-9 pr-9 bg-white/5"
                data-testid="force-change-new-password"
                autoComplete="new-password" />
              <button type="button" onClick={() => setShowPw((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50" data-testid="force-change-pw-toggle">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest">Confirm new password</Label>
            <div className="relative mt-1">
              <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input type={showPw ? "text" : "password"} value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="pl-9 bg-white/5"
                data-testid="force-change-confirm-password"
                autoComplete="new-password" />
            </div>
            {confirmPw && confirmPw !== pw && (
              <div className="text-[11px] text-rose-300 mt-1" data-testid="force-change-pw-mismatch">Passwords don&apos;t match</div>
            )}
          </div>

          {err && (
            <div className="rounded-xl bg-rose-500/10 border border-rose-500/30 p-3 text-sm text-rose-200"
              data-testid="force-change-error">
              {err}
            </div>
          )}

          <Button type="submit" disabled={!canSubmit}
            className="w-full h-12 bg-gradient-primary text-white font-heading tracking-[0.2em] rounded-2xl"
            data-testid="force-change-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "UPDATE &amp; CONTINUE"}
          </Button>
        </form>

        <div className="text-center pt-2 border-t border-white/10">
          <button onClick={signOut} className="text-xs text-white/50 hover:text-rose-300 inline-flex items-center gap-1"
            data-testid="force-change-signout">
            <LogOut className="h-3 w-3" /> Sign out instead
          </button>
        </div>
      </motion.div>
    </div>
  );
}
